import { useState } from 'react';

import { useSelectCommonality, type AssistantKeywordSelectionMessage } from '@/entities/discovery';
import { Button } from '@/shared/components/shadcn/button';
import { cn } from '@/shared/utils';
import { ArrowRightIcon, Check } from 'lucide-react';
import { AssistantMessageRow } from './primitives';
import { useDiscoveryMessagesActions } from '../../stores/discovery-message-store';
import { buildAssistantLoadingMessage } from './build-message.helpers';

const KEYWORD_SELECTION_LIMIT = 5;

export const AssistantMessageKeywordSelection = ({
  message,
}: {
  message: AssistantKeywordSelectionMessage;
}) => {
  const { appendMessages, replaceMessages } = useDiscoveryMessagesActions();
  const selectCommonalityMutation = useSelectCommonality();
  const [selectedKeywords, setSelectedKeywords] = useState(
    () =>
      message.content.selected_post_feature_conditions ??
      message.content.candidate_post_feature_conditions.slice(0, 3),
  );

  const selectedCount = selectedKeywords.length;
  const reachedSelectionLimit = selectedCount >= KEYWORD_SELECTION_LIMIT;

  const toggleKeyword = (keyword: string) => {
    setSelectedKeywords((currentKeywords) => {
      if (currentKeywords.includes(keyword)) {
        return currentKeywords.filter((currentKeyword) => currentKeyword !== keyword);
      }

      if (currentKeywords.length >= KEYWORD_SELECTION_LIMIT) return currentKeywords;

      return [...currentKeywords, keyword];
    });
  };

  const handleSubmit = async () => {
    replaceMessages([
      {
        ...message,
        content: {
          ...message.content,
          selected_post_feature_conditions: selectedKeywords,
        },
      },
    ]);
    appendMessages([buildAssistantLoadingMessage(message.discovery_session_id)]);
    const res = await selectCommonalityMutation.mutateAsync({
      discoverySessionId: message.discovery_session_id,
      request: {
        message_id: message.message_id,
        selected_post_feature_conditions: selectedKeywords,
      },
    });
    replaceMessages([res]);
  };

  const isSubmitted = Boolean(message.content.selected_post_feature_conditions);

  return (
    <AssistantMessageRow>
      <div className="mt-1 flex flex-1 flex-col gap-3">
        <span className="text-primary text-sm">{message.content.message}</span>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-foreground text-sm font-medium">
            Choose up to {KEYWORD_SELECTION_LIMIT} keywords
          </p>
          <p className="text-muted-foreground text-xs">
            {selectedCount}/{KEYWORD_SELECTION_LIMIT} selected
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {message.content.candidate_post_feature_conditions.map((keyword) => {
            const isSelected = selectedKeywords.includes(keyword);
            const isDisabled = isSubmitted || (!isSelected && reachedSelectionLimit);

            return (
              <button
                key={keyword}
                type="button"
                onClick={() => toggleKeyword(keyword)}
                disabled={isDisabled}
                className={cn(
                  'rounded-full border px-3 py-1.5 text-xs font-medium whitespace-nowrap transition-colors',
                  isSelected
                    ? 'border-foreground bg-foreground text-background'
                    : 'border-border bg-background text-foreground hover:bg-muted',
                  isDisabled && 'cursor-not-allowed opacity-60',
                )}
              >
                <span className="flex items-center gap-1.5">
                  <span
                    className={cn(
                      'flex size-3.5 shrink-0 items-center justify-center rounded-full border transition-colors',
                      isSelected
                        ? 'border-background/70 bg-background/15 text-background'
                        : 'border-muted-foreground/40 bg-background text-transparent',
                    )}
                  >
                    <Check
                      className={cn('size-2.5 shrink-0', isSelected ? 'opacity-100' : 'opacity-0')}
                    />
                  </span>
                  {keyword}
                </span>
              </button>
            );
          })}
        </div>
        <div className="flex justify-start pt-1">
          <Button
            type="button"
            size="sm"
            disabled={isSubmitted || selectedCount === 0}
            onClick={handleSubmit}
          >
            Find creators
            <ArrowRightIcon className="size-4" />
          </Button>
        </div>
      </div>
    </AssistantMessageRow>
  );
};
